'use client';

import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { KBareButton } from './button';
import { cx } from './cx';

/**
 * СВОРАЧИВАЕМАЯ КАРТОЧКА НАБОРА (PAY-FLOW-TZ §16 К16, образец владельца — карточка «Context window» листа «Usage»
 * приложения Claude): карточка вида `KListGroup`, первая строка — шапка на всю ширину: слово жирно слева, справа
 * значение и шеврон (поворот на 180° при раскрытии). Тап по шапке раскрывает/сворачивает; под шапкой — тонкая линия
 * и тело (`children`, обычно строки `KListRow`): строки ПЛОТНЫЕ и БЕЗ волосков между ними (как на образце). Подвала с
 * итогом внизу нет (владелец 10.10, приёмка К18: «дублирование не нужно», PAY-FLOW-TZ §18.9 п.1). Шапка с подстрокой —
 * поля строки набора с подстрокой (`k-lrow--has-hint`); подстрока из нескольких строк — каждая своим `<span>` с новой
 * строки. Свёрнуто — только шапка.
 *
 * Раскрытие — высота 0 → высота содержимого (замер `scrollHeight`) за 0,25 с по `--m-ease`, по концу хода — `auto`
 * (тело дальше растёт по содержимому без прыжка); сворачивание — обратно от замеренной высоты к 0. Шеврон крутится
 * тем же временем. `aria-expanded` у шапки, `aria-controls` — на тело.
 */
export function KDisclosure({
  title,
  hint,
  value,
  defaultOpen = false,
  className,
  children,
}: {
  title: ReactNode;
  /** Серая подстрока под заголовком шапки (образец: «209.2k of 1M tokens» под «Context window»). */
  hint?: ReactNode;
  value?: ReactNode;
  defaultOpen?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const id = useId();
  const [open, setOpen] = useState(defaultOpen);
  const body = useRef<HTMLDivElement | null>(null);
  const first = useRef(true);
  useLayoutEffect(() => {
    const el = body.current;
    if (!el) return;
    if (first.current) {
      first.current = false;
      el.style.height = open ? 'auto' : '0px';
      return;
    }
    const end = (e: TransitionEvent): void => {
      if (e.target !== el || e.propertyName !== 'height') return;
      if (open) el.style.height = 'auto';
    };
    el.addEventListener('transitionend', end);
    if (open) {
      el.style.height = '0px';
      void el.offsetHeight;
      el.style.height = `${el.scrollHeight}px`;
    } else {
      el.style.height = `${el.scrollHeight}px`;
      void el.offsetHeight;
      el.style.height = '0px';
    }
    // Хода нет (уменьшенное движение, кадров нет) — сразу конечное значение.
    const dur = parseFloat(getComputedStyle(el).transitionDuration || '0');
    if (!dur && open) el.style.height = 'auto';
    return () => el.removeEventListener('transitionend', end);
  }, [open]);
  return (
    <div className={cx('k-lgroup k-disc', open && 'is-open', className)}>
      <KBareButton
        className={cx('k-lrow k-disc__head', hint != null && hint !== '' && 'k-lrow--has-hint')}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="k-lrow__main">
          <span className="k-lrow__t">{title}</span>
          {hint != null && hint !== '' ? <span className="k-lrow__hint">{hint}</span> : null}
        </span>
        {value != null && value !== '' ? <span className="k-lrow__val">{value}</span> : null}
        <svg className="k-disc__chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </KBareButton>
      <div ref={body} id={id} className="k-disc__body" aria-hidden={!open || undefined} inert={!open || undefined}>
        <div className="k-disc__line" />
        <div className="k-disc__rows">{children}</div>
      </div>
    </div>
  );
}
